import ChannelFavorite from '@@types/ChannelFavorite';
import Favorite from '@@types/Favorite';
import FolderFavorite from '@@types/FolderFavorite';

import { FAVORITE_TYPE, TableNames } from '~/constants';
import { isDummyChannelId } from '~/services/channelService';
import { channelsStore } from '~/stores/ChannelsStore';
import { favoritesStore } from '~/stores/FavoritesStore';
import { api } from '~/stores/providers/Api2';
import idb from '~/stores/providers/Idb';
import { userStore } from '~/stores/UserStore';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import { mMaybe, parallelFutures, runFutureValue } from '~/utils/monadUtil';
import { encaseP, FutureInstance } from 'fluture';
import {
  append,
  assoc,
  findIndex,
  insert,
  map,
  mergeRight,
  objOf,
  pipe,
  pluck,
  prop,
  propEq,
  reduce,
  reject,
  replace,
  sequence,
} from 'ramda';
import { Maybe } from 'ramda-fantasy';

import Actions from '../Actions';

type OpenedFlag = {
  id: string;
  data: any;
};

class FavoriteActions {
  constructor(private actions: Actions) {}

  addFolder(title: string) {
    return api.channel.setFavorite({
      userId: userStore.id,
      title,
      type: FAVORITE_TYPE.FOLDER,
    });
  }

  private fetchOpenedFlag = (folderId: string): FutureInstance<never, OpenedFlag> => {
    return pipe<any, any, any, any>(
      encaseP(idb.get(TableNames.FavoriteFolderOpened)),
      map(objOf('data')),
      map(mergeRight({ data: true, id: folderId }))
    )(folderId);
  };

  private setOpendFlagsFromResponse = (openedFlags: OpenedFlag[]) => {
    pipe<any, any, any, any>(
      (of: OpenedFlag[]) => keyBy('id', of),
      pluck('data'),
      favoritesStore.setOpenedFlagMap
    )(openedFlags);
  };

  private setFavoriteChannel = (channelId: string) => {
    return api.channel.setFavorite({
      channelId,
      type: FAVORITE_TYPE.CHANNEL,
      userId: userStore.id,
    });
  };

  private unsetChannelInRootByChannelId = (channelId: string) => {
    return pipe<any, any, any, any>(
      favoritesStore.safeGetChannelByChannelId,
      map(prop('id')),
      mMaybe(Promise.resolve(), (favoriteId: string) => api.channel.unsetFavorite(favoriteId, true, userStore.id))
    )(channelId);
  };

  private unsetChannelInFolder = (channelId: string) => {
    return pipe<any, any, any>(
      favoritesStore.getFolderByChannelId,
      mMaybe(Promise.resolve(), (folder: FolderFavorite) =>
        api.channel.unsetFavoriteInFolder(folder.id, channelId, userStore.id)
      )
    )(channelId);
  };

  private unsetFavoriteChannel = (channelId: string) => {
    if (favoritesStore.isRootFavoriteByChannelId(channelId)) {
      return this.unsetChannelInRootByChannelId(channelId);
    } else {
      return this.unsetChannelInFolder(channelId);
    }
  };

  fetchOpenedFlags = (folderIds: string[]) => {
    pipe<any, any, any, any>(
      map(this.fetchOpenedFlag),
      parallelFutures(folderIds.length),
      runFutureValue(this.setOpendFlagsFromResponse)
    )(folderIds);
  };

  moveChannelToAfterChannel = (sourceId: string, targetId: string): void => {
    const folder = favoritesStore.getFolderByChannelFavoriteId(targetId);
    const source = favoritesStore.safeGetChannelById(sourceId);
    pipe<any, any, any>(
      sequence(Maybe.of),
      map(([source, folder]) => {
        favoritesStore.removeChannel(source);
        const index = findIndex(propEq('id', targetId), folder.channels);
        favoritesStore.setFolderChannels(insert(index + 1, source, folder.channels), folder.id);
      })
    )([source, folder]);
  };

  moveChannelToBeforeChannel = (sourceId: string, targetId: string): void => {
    const folder = favoritesStore.getFolderByChannelFavoriteId(targetId);
    const source = favoritesStore.safeGetChannelById(sourceId);
    pipe<any, any, any>(
      sequence(Maybe.of),
      map(([source, folder]) => {
        favoritesStore.removeChannel(source);
        const index = findIndex(propEq('id', targetId), folder.channels);
        favoritesStore.setFolderChannels(insert(index, source, folder.channels), folder.id);
      })
    )([source, folder]);
  };

  moveChannelToFolder = (sourceId: string, folderId: string) => {
    pipe<any, any, any>(
      favoritesStore.safeGetChannelById,
      map((source: any) => {
        favoritesStore.removeChannel(source);
        favoritesStore.addChannelToFolder(source, folderId);
      })
    )(sourceId);
  };

  setOpenFlag = (folderId, value) => {
    const newOpenedMap = assoc(folderId, value, favoritesStore.openedMap);
    favoritesStore.setOpenedFlagMap(newOpenedMap);
    idb.put(TableNames.FavoriteFolderOpened, folderId, value);
  };

  toggleChannel = async (channelId: string) => {
    if (isDummyChannelId(channelId)) {
      const opponentId = replace('dummy-', '', channelId);
      const channel = await this.actions.channelActions.createDirectChannel(opponentId, false);
      channelsStore.addChannel(channel);
      return this.setFavoriteChannel(channel.channelId);
    } else {
      if (favoritesStore.isFavoriteChannel(channelId)) {
        return this.unsetFavoriteChannel(channelId);
      } else {
        return this.setFavoriteChannel(channelId);
      }
    }
  };

  unsetFavoriteFolder = (favoariteId: string) => {
    api.channel.unsetFavorite(favoariteId, true, userStore.id);
  };

  swapChannels = (sourceId, targetId) => {
    const maybeDatas = [
      favoritesStore.safeGetChannelById(sourceId),
      favoritesStore.safeGetChannelById(targetId),
      favoritesStore.getFolderByChannelFavoriteId(sourceId),
    ];
    pipe<any, any, any>(
      sequence(Maybe.of),
      map(([source, target, folder]) => {
        folder.channels = reduce(
          (result: Favorite[], channel: ChannelFavorite) => {
            return source.id === channel.id
              ? append(target, result)
              : target.id === channel.id
              ? append(source, result)
              : append(channel, result);
          },
          [],
          folder.channels
        );
      })
    )(maybeDatas);
  };

  swapFolders = (sourceId: string, targetId: string) => {
    const sourceFolder = favoritesStore.safeGetFolderById(sourceId);
    const targetFolder = favoritesStore.safeGetFolderById(targetId);
    pipe<any, any, any, any>(
      sequence(Maybe.of),
      map(([source, target]) => {
        return reduce(
          (result: FolderFavorite[], favorite: FolderFavorite) => {
            return sourceId === favorite.id
              ? append(target, result)
              : targetId === favorite.id
              ? append(source, result)
              : append(favorite, result);
          },
          [],
          favoritesStore.folders
        );
      }),
      map(favoritesStore.setFolders)
    )([sourceFolder, targetFolder]);
  };

  updateOrder = () => {
    const { folders, rootChannels } = favoritesStore;
    api.channel.updateFavoriteOrder({
      userId: userStore.id,
      folders: reject(propEq('id', 'defaultFolder'), folders),
      channels: rootChannels,
    });
  };

  updateTitle = (title: string, folderId: string) => {
    api.channel.updateFolderFavoriteTitle({
      userId: userStore.id,
      folderId,
      title,
    });
  };
}

export default FavoriteActions;
